import { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import * as stateActions from '../../actions/stateActions';

// Define error notification priority categories
const ERROR_CATEGORIES = {
	CRITICAL: ['Authentication failed', 'Permission denied', 'Microphone', 'access denied'],
	CONNECTION: ['Connection failed', 'Network', 'transport', 'timed out'],
	MEDIA: ['WebRTC', 'ICE', 'audio', 'media', 'playback']
};

const Notifications = () => {
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const dispatch = useDispatch();
	const notifications = useSelector(state => state.notifications || []);
	const displayedRef = useRef([]);
	const activeErrorsRef = useRef(new Set());

	const storeDisplayed = (id) => {
		displayedRef.current = [...displayedRef.current, id];
	};

	const removeNotification = useCallback((notificationId) => {
		dispatch(stateActions.removeNotification(notificationId));
	}, [dispatch]);
	
	// Helper to categorize error notifications by priority
	const getErrorCategory = (notification) => {
		if (notification.type !== 'error') return null;
		
		const text = notification.text.toLowerCase();
		
		if (ERROR_CATEGORIES.CRITICAL.some(keyword => text.includes(keyword.toLowerCase()))) {
			return 'critical';
		} else if (ERROR_CATEGORIES.CONNECTION.some(keyword => text.includes(keyword.toLowerCase()))) {
			return 'connection';
		} else if (ERROR_CATEGORIES.MEDIA.some(keyword => text.includes(keyword.toLowerCase()))) {
			return 'media';
		}
		
		return 'general';
	};
	
	// Manage displayed notifications
	useEffect(() => {
		notifications.forEach((notification) => {
			// Do nothing if snackbar is already displayed
			if (displayedRef.current.includes(notification.id)) return;
			
			const isError = notification.type === 'error';
			const errorCategory = isError ? getErrorCategory(notification) : null;
			
			// For error notifications, limit concurrent similar errors
			if (isError) {
				// Clear similar existing error notifications to avoid overwhelming the user
				// but keep critical errors visible
				if (errorCategory !== 'critical') {
					// If we already have errors in the same category, close them before showing new one
					activeErrorsRef.current.forEach(id => {
						const displayedIndex = displayedRef.current.findIndex(dispId => dispId === id);
						if (displayedIndex !== -1) {
							closeSnackbar(id);
						}
					});
				}
				
				// Track this error
				activeErrorsRef.current.add(notification.id);
			}
			
			// Customize display settings based on notification type
			const anchorOrigin = isError
				? { vertical: 'top', horizontal: 'center' }  // Errors at top center
				: { vertical: 'bottom', horizontal: 'left' }; // Other notifications at bottom left
			
			// Display snackbar using notistack
			enqueueSnackbar(notification.text, {
				variant: notification.type,
				autoHideDuration: notification.timeout,
				anchorOrigin,
				onExited: () => {
					if (isError) {
						activeErrorsRef.current.delete(notification.id);
					}
				}
			});
			
			// Keep track of snackbars that we've displayed
			storeDisplayed(notification.id);
			
			// Dispatch action to remove snackbar from redux store
			removeNotification(notification.id);
		});
	}, [notifications, enqueueSnackbar, closeSnackbar, dispatch, removeNotification]);

	return null;
};

export default Notifications;
